import { IchatMsg, IchatRoom, IpeerInfo } from '../../../shared/core-types/chat';
import { TchatMsgStatus, TchatMsgWho } from '../../../shared/core-types/union.types';
import { EWIRE_TALK_ERROR_TYPE, WireTalkErrorHandler } from '../../../shared/utils/wire-talk-error';

/**
 * Base class for chat-related entities with shared properties.
 *
 * @remarks
 * - Ensures every entity has a stable `id` and `createTime`.
 * - Consumers should extend this for shared validation and metadata.
 */
export abstract class ChatRoomBase {
  /** Unique identifier for the chat entity */
  readonly id: string;

  /** Timestamp of entity creation */
  readonly createTime: Date;

  /**
   * Initializes a new chat entity.
   *
   * @param data - Base data for the chat entity.
   * @throws Validation error if `id` is missing.
   */
  constructor(data: Partial<IchatRoom | IchatMsg>) {
    if (!data.id) {
      throw WireTalkErrorHandler.create(
        'Chat entity must have an ID',
        EWIRE_TALK_ERROR_TYPE.VALIDATION_ERROR
      );
    }

    this.id = data.id;
    this.createTime = data.createTime || new Date();
  }

  /**
   * Validates if the provided data is non-empty.
   *
   * @typeParam T - Target shape expected for the data.
   * @param data - Data to validate.
   * @returns `true` if the data object has at least one own key; otherwise `false`.
   *
   * @example
   * if (!ChatRoomBase.validateData(room)) {
   *   throw new Error('Invalid room');
   * }
   */
  protected static validateData<T>(data: Partial<T>): data is T {
    return Object.keys(data).length > 0;
  }
}

/**
 * Manages chat room state and interactions.
 *
 * @remarks
 * - Tracks participants, block list, unread counts, and last activity.
 * - Use {@link ChatRoom.update} to mutate state; it supports add/remove semantics.
 *
 * @example
 * const room = new ChatRoom({ id: 'r1', peers: [], blocked: [] });
 * room.update({ peers: [{ id: 'u1' }] }, true); // add peer
 * room.update({ peers: [{ id: 'u1' }] }, false); // remove peer
 */
export class ChatRoom extends ChatRoomBase {
  /** Timestamp of most recent activity */
  lastActive: Date;

  /** Participants in the chat room */
  peers: IpeerInfo[];

  /** Users blocked from the room */
  blocked: string[];

  /** Count of unread messages */
  unviewedMsgsLength = 0;

  /** Classification of the chat room */
  type: string;

  /** Additional room metadata */
  extras?: unknown;

  /** Indicates room's active status */
  closed = false;

  /**
   * Creates a new chat room instance.
   *
   * @param room - Room configuration data.
   * @throws Validation error for invalid data (e.g., empty object or missing id).
   */
  constructor(room: Partial<IchatRoom<IpeerInfo>>) {
    super(room);

    if (!ChatRoomBase.validateData(room)) {
      throw WireTalkErrorHandler.create(
        'Invalid room data provided',
        EWIRE_TALK_ERROR_TYPE.VALIDATION_ERROR,
        { roomData: room }
      );
    }

    this.lastActive = room.lastActive || new Date();
    this.peers = room.peers || [];
    this.blocked = room.blocked || [];
    this.unviewedMsgsLength = room.unviewedMsgsLength || 0;
    this.type = room.type || 'default';
    this.extras = room.extras;
    this.closed = room.closed || false;
  }

  /**
   * Adjusts the unread message count by a delta.
   *
   * @param val - Positive or negative delta to apply to `unviewedMsgsLength`.
   */
  updateUnviewedMsgsLength(val: number) {
    this.unviewedMsgsLength += val;
  }

  /**
   * Updates room state with new information.
   *
   * @param val - New room values. `unviewedMsgsLength` is ignored if provided.
   * @param add - When `true`, appends `peers`/`blocked` to current lists; when `false`, removes provided entries from the current lists.
   * @returns The updated room instance (for chaining).
   *
   * @remarks
   * - `lastActive` is set to `val.lastActive` if present; otherwise remains unchanged.
   * - If `val.peers` is provided:
   *   - `add = true` → `peers = [...peers, ...val.peers]`.
   *   - `add = false` → removes peers whose `id` appears in `val.peers`.
   * - If `val.blocked` is provided:
   *   - `add = true` → `blocked = [...blocked, ...val.blocked]`.
   *   - `add = false` → removes entries included in `val.blocked`.
   */
  update(val: Partial<IchatRoom<IpeerInfo>>, add = false) {
    // this is handled else where
    if (val.unviewedMsgsLength) {
      delete val.unviewedMsgsLength;
    }

    this.lastActive = val.lastActive || this.lastActive;

    if (val.peers?.length) {
      const peers = this.peers || [];

      if (add) {
        this.peers = [...peers, ...val.peers];
      } else {
        this.peers = peers.filter(p => val.peers && !val.peers.map(value => value.id).includes(p.id));
      }
    }

    if (val.blocked?.length) {
      const blocked = this.blocked || [];

      if (add) {
        this.blocked = [...blocked, ...val.blocked];
      } else {
        this.blocked = blocked.filter(b => val.blocked && !val.blocked.includes(b));
      }
    }

    return this;
  }

  /**
   * Retrieves room participants.
   * @returns List of peer information
   */
  getParticipants() {
    return this.peers;
  }

  /**
   * Finds a specific peer by ID.
   * @param id Peer identifier
   * @returns Peer information or undefined
   */
  getPeerById(id: string) {
    return this.peers.find(p => p.id === id);
  }
}

/**
 * Represents a message within a chat room.
 *
 * @remarks
 * - Determines `who` from the perspective of the current user: `'me'` vs `'partner'`.
 * - `status` defaults to `'pending'` when not provided.
 */
export class ChatMsg extends ChatRoomBase {
  /** Information about the message sender */
  peerInfo?: IpeerInfo;

  /** Identifier of the containing room */
  roomId: string;

  /** Message text content */
  msg: string;

  /** Indicates message sender perspective */
  who: TchatMsgWho;

  /** Current message transmission status */
  status: TchatMsgStatus;

  /** Indicates if message has been deleted */
  deleted: boolean;

  /**
   * Creates a new chat message instance.
   *
   * @param myId - Current user's ID.
   * @param msg - Message configuration data.
   * @throws Validation error for invalid data (e.g., empty object or missing id).
   *
   * @example
   * const m = new ChatMsg('u1', { id: 'm1', roomId: 'r1', msg: 'Hi' });
   * console.log(m.who); // 'me' or 'partner'
   */
  constructor(
    private myId: string,
    msg: Partial<IchatMsg<IpeerInfo>>
  ) {
    super(msg);

    if (!ChatRoomBase.validateData(msg)) {
      throw WireTalkErrorHandler.create(
        'Invalid message data provided',
        EWIRE_TALK_ERROR_TYPE.VALIDATION_ERROR,
        { messageData: msg }
      );
    }

    this.peerInfo = msg.peerInfo;
    this.roomId = msg.roomId || '';
    this.msg = msg.msg || '';
    this.who = this.myId === this.peerInfo?.id ? 'me' : 'partner';
    this.status = msg.status || 'pending';
    this.deleted = msg.deleted || false;
  }
}
